import Image from 'next/image'
import React from 'react'
import useCardPrice from 'src/hooks/useCardPrice'
import { getDiscount } from 'src/utils/coverPhoto'
import { ILocation } from '../../types/graphql'
import { numberRounedUpWithoutDecimals } from '../../utils'

interface SpotCardProps {
  location: ILocation
  onClick: (marker: ILocation) => void
  isSelected: boolean
}

const LocationMapButton = ({
  location,
  onClick,
  isSelected
}: SpotCardProps) => {
  const { defaultConfiguration } = location ?? {}

  const handleOnClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation()
    onClick(location)
  }

  const format = numberRounedUpWithoutDecimals
  const { price, isBrokerMode } = useCardPrice(location)
  // Always the REGULAR monthly price. The pin used to print the halved
  // first-month price («$X | 1er mes») with no «6+ meses» condition — a price
  // a 2-month renter is never billed (getDiscount's rule: any surface showing
  // the discount must show minMonths, and a pin has no room for it). The pin
  // now only flags that a promotion exists; the map card spells it out.
  const textPrice = isBrokerMode
    ? `$${format(price)}/m²`
    : price >= 10000
      ? `$${format(price / 1000)}k`
      : `$${format(price)}`
  const hasPromo =
    Boolean(getDiscount(defaultConfiguration, isBrokerMode)) ||
    Boolean(defaultConfiguration?.operatorPromo)

  return (
    <button
      onClick={handleOnClick}
      className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap shadow-[0_2px_8px_-2px_rgba(18,38,63,0.25)] transition-all duration-200 hover:scale-110 ${
        isSelected
          ? 'bg-black-600 text-white-500'
          : 'bg-blue-500 text-white-500'
      }`}
    >
      <Image
        src="/logos/logo-icon-white.png"
        alt="SpotMe"
        width={15}
        height={15}
        className="-ml-0.5"
        style={{ width: 15, height: 15 }}
      />
      <span>{textPrice}</span>
      {/* Same «rosa mexicano» as the card badge, so a promo pin is findable
          at a glance on a crowded map. */}
      {hasPromo && (
        <span className="-mr-1 rounded-full bg-promo-600 px-1.5 py-px text-[10px] leading-tight font-bold text-white-500">
          Promo
        </span>
      )}
    </button>
  )
}

export default React.memo(LocationMapButton)
